// recovery_phrase.ts — BIP39 helpers para recovery de cuenta v016.
//
// Modelo v112:
//  - Al registro generamos 24 palabras (256 bits de entropia).
//  - Se MUESTRAN al usuario una sola vez. Es responsable de guardarlas.
//  - El derivado servira en v113+ para recovery completo (descifrar archive
//    sin password local). En v112 solo se almacena la posibilidad.
//  - Se guarda cifrada con la wrappingKey ACTUAL en localStorage para que el
//    usuario pueda volver a verlas mientras tenga su password.

import { generateMnemonic, validateMnemonic, mnemonicToSeedSync } from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/english.js';
import * as crypto from './crypto';

// 256 bits de entropia -> 24 palabras.
export function generateRecoveryPhrase(): string {
  return generateMnemonic(wordlist, 256);
}

export function isValidRecoveryPhrase(phrase: string): boolean {
  try {
    return validateMnemonic(phrase.trim().toLowerCase().replace(/\s+/g, ' '), wordlist);
  } catch { return false; }
}

// master_seed = BIP39 seed de 64 bytes. Sera la base del modelo v113 (master_vault_key).
export function recoveryPhraseToMasterSeed(phrase: string): Uint8Array {
  return mnemonicToSeedSync(phrase.trim().toLowerCase().replace(/\s+/g, ' '));
}

// Derivacion de la master_vault_key (32 bytes) desde el seed.
// Reemplazara wrappingKey = Argon2id(password) en v113+.
export function deriveMasterVaultKey(seed: Uint8Array): Uint8Array {
  const salt = new Uint8Array(0);
  const info = new TextEncoder().encode('syphrax-master-vault-v1');
  return crypto.hkdf(seed, salt, info, 32);
}

const STORAGE_KEY = 'syphrax_recovery_phrase_enc_v1';

// Guarda la phrase cifrada con wrappingKey en localStorage del device.
export function persistRecoveryPhrase(phrase: string, wrappingKey: Uint8Array): void {
  const wrapped = crypto.wrapPrivateKey(new TextEncoder().encode(phrase), wrappingKey);
  const blob = new Uint8Array(wrapped.nonce.length + wrapped.ciphertext.length);
  blob.set(wrapped.nonce, 0);
  blob.set(wrapped.ciphertext, wrapped.nonce.length);
  try { localStorage.setItem(STORAGE_KEY, crypto.b64encode(blob)); } catch {}
}

// Recupera la phrase descifrandola con wrappingKey.
export function loadRecoveryPhrase(wrappingKey: Uint8Array): string | null {
  try {
    const b64 = localStorage.getItem(STORAGE_KEY);
    if (!b64) return null;
    const blob = crypto.b64decode(b64);
    const plain = crypto.unwrapPrivateKey(blob, wrappingKey);
    return new TextDecoder().decode(plain);
  } catch { return null; }
}
